import SystemConfig, { CONFIG_PARAMS } from "./system/SystemConfig";
import { errorMessages, MESSAGE_EVENTS, MESSAGE_TYPES } from "./error/errorMessages";
import { ERROR_CODES } from "./error/errorCodes";
import MadmaxMessageCooldown from "./ui/messages/MadmaxMessageCooldown";
import MadmaxMessage from "./ui/messages/MadmaxMessage";
import { inject, injectable } from "inversify";
import SERVICE_IDENTIFIER from "./constants/identifiers";
import type IUIMessages from "./ui/messages/IUIMessages";
import type IMadmaxMessage from "./ui/messages/IMadmaxMessage";
import { translate } from "./translations/translations";
import MadmaxError from "./error/MadmaxError";
import { ACTION_EVENTS } from "./ui/UserAction";

@injectable()
export default class Messages {

    private _config: SystemConfig;
    private _messages: Record<string, IMadmaxMessage>;

    public uiMessages: IUIMessages;

    constructor(
        @inject(SERVICE_IDENTIFIER.SystemConfig) config: SystemConfig,
        @inject(SERVICE_IDENTIFIER.UIMessages) uiMessages: IUIMessages
    ) {

        this._config = config;
        this.uiMessages = uiMessages;

        /**
         * @type {Object<MadmaxMessage>}
         */
        this._messages = {};

    }

    getErrorMessage(madmaxError: MadmaxError, lang = this._config.get(CONFIG_PARAMS.LANGUAGE)): IMadmaxMessage {

        if (this._messages[madmaxError.code]) {
            return this._messages[madmaxError.code];
        }

        let em = this._createErrorMessage(madmaxError, lang);
        this._messages[madmaxError.code] = em;
        return em;
    }

    _createErrorMessage(madmaxError, lang = this._config.get(CONFIG_PARAMS.LANGUAGE)) {
        let code = madmaxError.code;
        let msg = madmaxError.msg;

        let type = MESSAGE_TYPES.ERROR;
        let zIndex = 100;
        let title = ""; // { msg: { [lang]: "" } };
        let content = msg; // { msg: { [lang]: msg } };
        let buttons = {};

        if (errorMessages[code]) {
            let em = errorMessages[code];

            buttons = em.buttons;
            type = em.type;

            // @ts-ignore
            if (em.zIndex) zIndex = em.zIndex;

            if (em.content && em.content.msg["$translate"]) {
                content = translate(em.content.msg["$translate"], lang);
            }

            if (em.content && em.content.msg["$default"]) {
                content = em.content.msg["$default"];
            }

            if (em.content && em.content.msg[lang]) {
                content = em.content.msg[lang]
            }

            if (madmaxError.getShowMessageTag()) {
                content = madmaxError.msg;
            }

            if (em.title && em.title.msg[lang]) {
                title = em.title.msg[lang];
            }

            if (em.title && em.title.msg["$translate"]) {
                title = translate(em.title.msg["$translate"], lang);
            }
        }


        if (madmaxError.buttons && Array.isArray(madmaxError.buttons) && madmaxError.buttons.length > 0) {
             const isWarning = madmaxError.buttons.find(b => b?.action === 'continue');
            buttons = madmaxError.buttons.reduce((acc, button) => {
                let event;
                switch(button.action.toLowerCase()) {
                    case 'void':
                        event = MESSAGE_EVENTS.MESSAGE_CLOSE_GAME;
                        break;
                    case 'continue':
                        event = MESSAGE_EVENTS.MESSAGE_CLOSE;
                        break;
                    case 'history':
                        event = MESSAGE_EVENTS.OPEN_HISTORY;
                        break;
                    default:
                        event = button.action;
                }
                
                acc[button.text] = {
                    event: event
                };
                return acc;
            }, {});
            if(isWarning){
                type = MESSAGE_TYPES.WARNING;
            }
        }

        // console.log(zIndex, title, content, buttons, lang);

        let madmaxMessage;

        if (code === ERROR_CODES.COOLDOWN) {
            madmaxMessage = new MadmaxMessageCooldown(title, content, buttons, lang, type, zIndex, this._config, code);
        } else {
            madmaxMessage = new MadmaxMessage(title, content, buttons, lang, type, zIndex, this._config, code);
        }

        madmaxMessage.create();
        this.uiMessages.addMessage(madmaxMessage);

        return madmaxMessage;
    }
}